export interface WorkspaceOperationTracker {
	close: () => void;
	run: <T>(operation: () => Promise<T>) => Promise<T>;
	waitForIdle: (options: { timeoutMs: number }) => Promise<void>;
}

/** Tracks started mutations so a displaced runtime can retire after their continuations settle. */
export function createWorkspaceOperationTracker(): WorkspaceOperationTracker {
	const pending = new Set<Promise<unknown>>();
	let accepting = true;

	function close() {
		accepting = false;
	}

	async function run<T>(operation: () => Promise<T>): Promise<T> {
		if (!accepting) {
			throw new Error('Workspace runtime no longer accepts operations');
		}

		const promise = operation();
		pending.add(promise);

		try {
			return await promise;
		}
		finally {
			pending.delete(promise);
		}
	}

	async function waitForIdle({ timeoutMs }: { timeoutMs: number }): Promise<void> {
		if (pending.size === 0) return;

		let timeout: ReturnType<typeof setTimeout> | undefined;
		const timedOut = new Promise<void>((resolve) => {
			timeout = setTimeout(resolve, timeoutMs);
		});

		try {
			await Promise.race([Promise.allSettled(pending), timedOut]);
		}
		finally {
			if (timeout) clearTimeout(timeout);
		}
	}

	return { close, run, waitForIdle };
}
